import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@/lib/apiClient";
import { toast } from "sonner";

export interface ProdutoContabil {
  id: number;
  operacaoId: number;
  contaContabilDebitoId: number;
  contaContabilDebitoCodigo?: string | null;
  contaContabilDebitoDescricao?: string | null;
  contaContabilCreditoId: number;
  contaContabilCreditoCodigo?: string | null;
  contaContabilCreditoDescricao?: string | null;
  observacao?: string | null;
}

export interface SalvarProdutoContabilRequest {
  operacaoId: number;
  contaContabilDebitoId: number;
  contaContabilCreditoId: number;
  observacao?: string | null;
}

export const useProdutoContabil = (produtoId: string | undefined) => {
  const queryClient = useQueryClient();
  const numId = produtoId ? Number(produtoId) : undefined;
  const key = ["produto-contabil", numId];

  const query = useQuery({
    queryKey: key,
    queryFn: async () => {
      const { data } = await apiClient.get<ProdutoContabil[]>(`/api/produtos/${numId}/contabil`);
      return data;
    },
    enabled: !!numId,
  });

  const createMutation = useMutation({
    mutationFn: async (body: SalvarProdutoContabilRequest) => {
      if (!numId) throw new Error("Produto ID is required");
      const { data } = await apiClient.post<ProdutoContabil>(`/api/produtos/${numId}/contabil`, body);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: key });
      toast.success("Registro contábil adicionado!");
    },
    onError: (e: Error) => toast.error(`Erro ao adicionar: ${e.message}`),
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: number) => { await apiClient.delete(`/api/produtos/${numId}/contabil/${id}`); },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: key });
      toast.success("Registro contábil removido!");
    },
    onError: (e: Error) => toast.error(`Erro ao remover: ${e.message}`),
  });

  return {
    registros:  query.data ?? [],
    isLoading:  query.isLoading,
    createReg:  createMutation.mutate,
    deleteReg:  deleteMutation.mutate,
    isCreating: createMutation.isPending,
  };
};
